// KORA-125: the emailed deletion confirmation link carries its secret in the URL fragment
// (`/account/delete/confirm#deletion=<id>&token=<t>`), so it never reaches a server log or a
// Referer header. It is moved into sessionStorage before the app starts and the address bar is
// cleared (the `membership-confirmation.ts` pattern), so the secret survives the sign-in redirect
// a step-up needs without sitting in the URL.
const STORAGE_KEY = 'kora-deletion-confirmation'
const CONFIRMATION_PATH = '/account/delete/confirm'

export interface StoredDeletionConfirmation {
  deletionId: string
  token: string
}

/** Move the fragment-only secret out of the address bar before auth initialization. */
export function captureDeletionConfirmationFragment(): void {
  if (window.location.pathname !== CONFIRMATION_PATH || !window.location.hash) return
  const params = new URLSearchParams(window.location.hash.slice(1))
  const deletionId = params.get('deletion')
  const token = params.get('token')
  try {
    sessionStorage.removeItem(STORAGE_KEY)
    if (deletionId && token && /^[A-Za-z0-9_-]{43}$/.test(token)) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ deletionId, token }))
    }
  } catch {
    // Private browsing can disable storage; the view will show a missing-link state.
  } finally {
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search
    )
  }
}

export function readDeletionConfirmation(): StoredDeletionConfirmation | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    const value: unknown = JSON.parse(stored)
    if (
      typeof value !== 'object' ||
      value === null ||
      !('deletionId' in value) ||
      !('token' in value)
    )
      return null
    if (typeof value.deletionId !== 'string' || typeof value.token !== 'string') return null
    return { deletionId: value.deletionId, token: value.token }
  } catch {
    return null
  }
}

export function clearDeletionConfirmation(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage was unavailable; the in-memory view state is still cleared.
  }
}
